import { useVerificationModal } from '@/features/user-verification';
import { ModalComponentWrapper, Button } from '@/shared/components';
import { useContactUs, useModalStore } from '@/shared/lib';
import { useSessionStore } from '@/entities/session';
import { useVerificationStatus } from '@/widgets/user-verification-cta/model/useVerificationStatus';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  title?: string;
  onBack?: () => void;
}

export function VerificationInfoModal({ title: propTitle, onBack }: Props) {
  const { t } = useTranslation();
  const { openVerificationModal } = useVerificationModal();
  const { closeDialog } = useModalStore();
  const { session } = useSessionStore();

  const { links } = useContactUs({
    ewalletAccNum: session?.additional?.eWallet || '',
  });

  const {
    icon,
    title,
    description,
    buttonText,
    isVerificationRejected,
    isCorporateSuccess,
    isVerificationStart,
    isSumsubPending,
    isVerificationAwaiting,
  } = useVerificationStatus({ isBlackSuccessIcon: true });

  let modalTitle = title;
  let modalDescription = description;
  let modalButtonText = buttonText;

  if (isVerificationStart) {
    modalTitle = t('personalAccount.verificationRequired.title');
    modalDescription = t('personalAccount.verificationRequired.description');
    modalButtonText = t('personalAccount.verificationRequired.button');
  }

  if (isSumsubPending) {
    modalDescription = t('personalAccount.verificationCta.processDescription');
    modalButtonText = t('personalAccount.verificationCta.buttonPending');
  }

  if (isVerificationAwaiting) {
    modalTitle = t('personalAccount.verificationRequired.pendingTitle');
    modalDescription = t(
      'personalAccount.verificationRequired.pendingDescription'
    );
    modalButtonText = t('personalAccount.verificationRequired.pendingButton');
  }

  if (isCorporateSuccess) {
    modalButtonText = t('personalAccount.verificationRequired.corporateButton');
  }

  return (
    <ModalComponentWrapper
      title={propTitle ? propTitle : undefined}
      onBack={onBack ? onBack : undefined}
      contentClassName="overflow-x-hidden">
      <div
        className={
          'flex flex-col justify-between items-center gap-[2.4rem] max-h-[30rem] w-full max-w-[42rem] self-start'
        }>
        <div className="flex flex-col justify-start items-center gap-[2.4rem] w-full max-w-[42rem]">
          <img src={icon} className="w-[5.6rem]" alt="Verification status" />
          <div className="flex flex-col items-center justify-center gap-[0.8rem]">
            <span className="headline4 text-center">{modalTitle}</span>
            {(isCorporateSuccess || modalDescription) && (
              <p className="p4 text-center text-secondary">
                {isCorporateSuccess ? (
                  <Trans
                    i18nKey="personalAccount.verificationCta.corporateSuccessDescription"
                    components={{
                      email: <span className="text-primary font-medium" />,
                    }}
                  />
                ) : (
                  modalDescription
                )}
              </p>
            )}
          </div>
        </div>
        {modalButtonText && (
          <Button
            size="lg"
            variant={isVerificationAwaiting ? 'secondary' : 'default'}
            onClick={() => {
              if (isVerificationAwaiting) {
                closeDialog();
              } else if (isCorporateSuccess) {
                window.open(links?.verificationForCorporate || '', '_blank');
              } else if (isVerificationRejected) {
                window.open(links?.defaultRequest || '', '_blank');
              } else {
                openVerificationModal();
              }
            }}>
            {modalButtonText}
          </Button>
        )}
      </div>
    </ModalComponentWrapper>
  );
}
